import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useCompany } from "@/lib/theme";
import { APP_VERSION } from "@/lib/versions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowRight, ShieldCheck, Sparkle, MapTrifold } from "@phosphor-icons/react";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { company } = useCompany();
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!mobile || !password) return toast.error("Mobile and password required");
    setLoading(true);
    try {
      await login(mobile.trim(), password);
      toast.success("Welcome back");
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const brandName = company?.company_name || "SMIPL";
  const tagline = company?.tagline || "Trusted Service Partner";

  return (
    <div className="min-h-screen grid lg:grid-cols-2" style={{ background: "#0c1421" }}>
      {/* Left visual — dark + orange brand mark */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden text-white" style={{ background: "linear-gradient(135deg, #0c1421 0%, #111c2e 60%, #0e1726 100%)" }}>
        {/* Decorative glows */}
        <div aria-hidden className="absolute -top-32 -left-24 w-[420px] h-[420px] rounded-full" style={{ background: "radial-gradient(closest-side, rgba(253,126,20,0.30), transparent)" }} />
        <div aria-hidden className="absolute -bottom-32 -right-32 w-[520px] h-[520px] rounded-full" style={{ background: "radial-gradient(closest-side, rgba(253,126,20,0.18), transparent)" }} />
        <div aria-hidden className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.7) 1px, transparent 1px)", backgroundSize: "22px 22px" }} />

        <div className="relative flex items-center gap-3">
          <img src="/smipl-logo.jpg" alt="SMIPL" className="h-12 w-12 object-cover rounded-xl" />
          <div>
            <div className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>{brandName}</div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-white/55">{tagline}</div>
          </div>
        </div>

        <div className="relative">
          <h1 className="text-5xl xl:text-6xl font-semibold leading-[1.05] tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            Your trusted<br />
            <span className="text-[#fd7e14]">service partner</span><br />
            for success.
          </h1>
          <p className="mt-5 text-white/75 max-w-md text-base leading-relaxed">
            Enterprise CRM, payroll, attendance, expenses & inventory — built for SMIPL field teams, managers and leadership.
          </p>

          <div className="mt-10 flex flex-wrap gap-3">
            {[
              { icon: ShieldCheck, label: "ISO 9001:2015" },
              { icon: ShieldCheck, label: "ISO 27001:2022" },
              { icon: Sparkle, label: "Cloud-native" },
              { icon: MapTrifold, label: "Live GPS attendance" },
            ].map((b) => (
              <span key={b.label} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.06] border border-white/10 text-xs text-white/85">
                <b.icon size={14} weight="fill" className="text-[#fd7e14]" /> {b.label}
              </span>
            ))}
          </div>
        </div>

        <div className="relative text-xs text-white/45 flex items-center justify-between">
          <span>© {new Date().getFullYear()} {brandName}. All rights reserved.</span>
          <span className="text-white/35">Updated {new Date().toLocaleDateString("en-IN", { month: "short", year: "numeric" })}</span>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 lg:p-12 bg-white">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <img src="/smipl-logo.jpg" alt={brandName} className="h-12 w-12 object-cover rounded-xl" />
            <div>
              <div className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>{brandName}</div>
              <div className="text-xs uppercase tracking-[0.25em] text-slate-500">{tagline}</div>
            </div>
          </div>

          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
            Welcome back.
          </h2>
          <p className="text-sm text-slate-500 mt-1.5">Sign in with your registered mobile and password.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <Label htmlFor="mobile" className="text-xs uppercase tracking-wider text-slate-500">Mobile Number</Label>
              <Input id="mobile" data-testid="login-mobile-input" inputMode="numeric" placeholder="98XXXXXXXX"
                value={mobile} onChange={(e) => setMobile(e.target.value)} className="mt-1.5 h-12 text-base" autoFocus />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs uppercase tracking-wider text-slate-500">Password</Label>
                <Link to="/forgot" data-testid="forgot-link" className="text-xs text-[#fd7e14] hover:underline">
                  Forgot password?
                </Link>
              </div>
              <PasswordInput id="password" data-testid="login-password-input" placeholder="••••••••"
                value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-12 text-base" />
            </div>
            <Button data-testid="login-submit-button" type="submit" disabled={loading}
              className="w-full btn-brand h-12 text-base">
              {loading ? "Signing in…" : "Sign In"}
              <ArrowRight size={18} className="ml-2" />
            </Button>
          </form>

          <p className="mt-10 text-xs text-slate-500 text-center">
            Accounts are created by Finance Manager or Super Admin. No public registration.
          </p>
        </div>
      </div>
    </div>
  );
}
